import React from "react";
import { actionsApi } from "../services/api";
import type {
  EmailAction,
  SmsAction,
  RunQueryAction,
} from "../types/node_action";
import {
  Mail,
  MessageSquare,
  Database,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";

interface ActionsPageProps {}

export default function ActionsPage({}: ActionsPageProps) {
  const [loading, setLoading] = React.useState(true);
  const [emailActions, setEmailActions] = React.useState<EmailAction[]>([]);
  const [smsActions, setSmsActions] = React.useState<SmsAction[]>([]);
  const [runQueryActions, setRunQueryActions] = React.useState<
    RunQueryAction[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<
    "email" | "sms" | "run_query"
  >("email");

  const loadActions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [emailList, smsList, runQueryList] = await Promise.all([
        actionsApi.listEmailActions(),
        actionsApi.listSmsActions(),
        actionsApi.listRunQueryActions(),
      ]);

      setEmailActions(emailList);
      setSmsActions(smsList);
      setRunQueryActions(runQueryList);
    } catch (e: any) {
      setError(e?.message || "Failed to load actions");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadActions();
  }, [loadActions]);

  const deleteAction = async (
    type: "email" | "sms" | "run_query",
    id: number,
  ) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${type} action?`,
    );
    if (!confirmed) return;

    try {
      if (type === "email") {
        await actionsApi.deleteEmailAction(id);
        setEmailActions((prev) => prev.filter((a) => a.id !== id));
      } else if (type === "sms") {
        await actionsApi.deleteSmsAction(id);
        setSmsActions((prev) => prev.filter((a) => a.id !== id));
      } else if (type === "run_query") {
        await actionsApi.deleteRunQueryAction(id);
        setRunQueryActions((prev) => prev.filter((a) => a.id !== id));
      }
    } catch (e: any) {
      alert(`Failed to delete action: ${e?.message || "Unknown error"}`);
    }
  };

  const getActionIcon = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return <Mail className="h-5 w-5" />;
      case "sms":
        return <MessageSquare className="h-5 w-5" />;
      case "run_query":
        return <Database className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getActionCount = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return emailActions.length;
      case "sms":
        return smsActions.length;
      case "run_query":
        return runQueryActions.length;
      default:
        return 0;
    }
  };

  const getActions = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return emailActions;
      case "sms":
        return smsActions;
      case "run_query":
        return runQueryActions;
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Loading actions...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-lg text-red-600">Error: {error}</div>
            <button
              onClick={loadActions}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Actions Management
          </h1>
          <p className="mt-2 text-gray-600">
            Manage email, SMS, and run query actions that can be attached to
            conversation nodes.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8 border-b border-gray-200">
            {(["email", "sms", "run_query"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  {getActionIcon(tab)}
                  <span className="capitalize">{tab.replace("_", " ")}</span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {getActionCount(tab)}
                  </span>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Actions List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 capitalize">
                {activeTab.replace("_", " ")} Actions
              </h2>
              <Link
                to={`/actions/${activeTab}/new`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                New {activeTab.replace("_", " ")} Action
              </Link>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {getActions(activeTab).length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="text-gray-500">
                  No {activeTab.replace("_", " ")} actions found.
                </div>
                <Link
                  to={`/actions/${activeTab}/new`}
                  className="mt-2 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Create your first {activeTab.replace("_", " ")} action
                </Link>
              </div>
            ) : (
              getActions(activeTab).map((action) => (
                <ActionRow
                  key={action.id}
                  action={action}
                  type={activeTab}
                  onDelete={() => deleteAction(activeTab, action.id)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ActionRowProps {
  action: EmailAction | SmsAction | RunQueryAction;
  type: "email" | "sms" | "run_query";
  onDelete: () => void;
}

function ActionRow({ action, type, onDelete }: ActionRowProps) {
  const getActionIcon = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return <Mail className="h-5 w-5" />;
      case "sms":
        return <MessageSquare className="h-5 w-5" />;
      case "run_query":
        return <Database className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getActionDetails = (
    action: EmailAction | SmsAction | RunQueryAction,
    type: "email" | "sms" | "run_query",
  ) => {
    switch (type) {
      case "email":
        const emailAction = action as EmailAction;
        return (
          <div className="text-sm text-gray-600">
            <div>To: {emailAction.to_address || "Not set"}</div>
            <div>From: {emailAction.from_email || "Not set"}</div>
            {emailAction.subject && <div>Subject: {emailAction.subject}</div>}
          </div>
        );
      case "sms":
        const smsAction = action as SmsAction;
        return (
          <div className="text-sm text-gray-600">
            <div>To: {smsAction.to_address || "Not set"}</div>
            <div>Sender: {smsAction.sender_id || "Not set"}</div>
          </div>
        );
      case "run_query":
        const runQueryAction = action as RunQueryAction;
        return (
          <div className="text-sm text-gray-600">
            <div>Database: {runQueryAction.db_name}</div>
            <div className="truncate max-w-md">
              SQL: {runQueryAction.sql_text}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex-shrink-0 mt-1">{getActionIcon(type)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {action.name}
              </h3>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  action.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {action.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            {action.description && (
              <p className="mt-1 text-sm text-gray-500">{action.description}</p>
            )}
            {getActionDetails(action, type)}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/actions/${type}/${action.id}`}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
